<template>
  <SrpDropdown
    :class="{
      'caption-emoji-picker': true,
      'caption-emoji-picker--disabled': props.isDisabled,
    }"
    :placement="'top-start'"
    :offset="8"
    :width="320"
    :isMobileModalCloseButtonVisible="false"
  >
    <template #clickableElement>
      <button type="button" class="caption-emoji-picker__trigger" :disabled="props.isDisabled" aria-label="Add emoji">
        <IconEmbedded name="smile_2" :size="20" color="gray" />
      </button>
    </template>
    <template #dropdown="{ close: closeEmojiDropdown, isDropdownVisible }">
      <DropdownCloseBridge v-if="isDropdownVisible" :close="closeEmojiDropdown" @ready="closeDropdownRef = $event" />
      <emoji-picker v-if="isDropdownVisible" ref="domRefEmojiPicker" class="caption-emoji-picker__picker" data-source="/images/emoji/data.json" />
    </template>
  </SrpDropdown>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, defineComponent, onMounted, h } from "vue";
import "emoji-picker-element";

// Components
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const DropdownCloseBridge = defineComponent({
  props: {
    close: { type: Function, required: true },
  },
  emits: ["ready"],
  setup(props, { emit }) {
    onMounted(() => emit("ready", props.close));
    return () => h("span", { "aria-hidden": "true", style: "display:none" });
  },
});

const props = withDefaults(
  defineProps<{
    isDisabled?: boolean;
  }>(),
  {
    isDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "select", unicode: string): void;
}>();

const domRefEmojiPicker = ref<HTMLElement | null>(null);
const closeDropdownRef = ref<(() => void) | null>(null);

function handleEmojiClick(event: Event): void {
  const unicode = (event as CustomEvent<{ unicode: string }>).detail?.unicode;
  if (!unicode) return;
  emit("select", unicode);
  closeDropdownRef.value?.();
}

let removeEmojiClickListener: (() => void) | null = null;

watch(domRefEmojiPicker, picker => {
  removeEmojiClickListener?.();
  removeEmojiClickListener = null;
  if (!picker) return;
  picker.addEventListener("emoji-click", handleEmojiClick);
  removeEmojiClickListener = () => picker.removeEventListener("emoji-click", handleEmojiClick);
});

onBeforeUnmount(() => {
  removeEmojiClickListener?.();
});
</script>

<style scoped lang="scss">
.caption-emoji-picker {
  &--disabled {
    :deep(.dropdown-wrapper__clickable-element) {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__trigger {
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 6px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    color: rgba(0, 0, 0, 0.78);
    background: transparent;
    cursor: pointer;
    transition: background-color 0.07s ease-in-out;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__picker {
    --num-columns: 8;
    --emoji-size: 1.375rem;
    --border-radius: 8px;
    width: 320px;
    height: 360px;
  }
}
</style>
